// Onaylanan Belgeler — onay hiyerarşisi sürecinden geçip 'approved' duruma ulaşmış belgeler.
// Tıklanabilir → ilgili belge salt-okunur açılır. Onay geçmişi modal'ı statü rozetine tıklayınca açılır.
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { ListPageHeader, EmptyState, FilterBar, Button, Modal } from '../components/ui';
import DataTable from '../components/DataTable';
import { fmtAmount } from '../utils/numberFormat';

const MODULE_LABELS = { invoice: 'Fatura', waybill: 'İrsaliye', order: 'Sipariş', quote: 'Teklif' };
const DIRECTION_LABELS = { sales: 'Satış', purchase: 'Alış' };

const SOURCE_TO_PAGE = {
  sales_invoices:    { page: 'sales' },
  purchase_invoices: { page: 'purchases' },
  sales_waybills:    { page: 'waybills-sales' },
  purchase_waybills: { page: 'waybills-purchase' },
  sales_orders:      { page: 'orders-incoming' },
  purchase_orders:   { page: 'orders-outgoing' },
  sales_quotes:      { page: 'quotes-outgoing' },
  purchase_quotes:   { page: 'quotes-incoming' },
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR') : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('tr-TR') : '—';

export default function ApprovedDocumentsPage({ onNavigate } = {}) {
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ module: '', direction: '', date_from: '', date_to: '' });
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [historyModal, setHistoryModal] = useState(null); // { source_table, source_id, loading, data, error }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/approvals/approved', { params: { ...filters, limit: 500 } });
      if (r.data.success) { setList(r.data.data.items || []); setTotal(r.data.data.total || 0); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filters]);
  useEffect(() => { load(); }, [load]);

  const filtered = list.filter(r => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r.hierarchy_name || '').toLowerCase().includes(q)
        || (r.account_title || '').toLowerCase().includes(q)
        || (r.document_number || '').toLowerCase().includes(q)
        || (r.last_approver_name || '').toLowerCase().includes(q);
  });

  const openDoc = (row) => {
    const cfg = SOURCE_TO_PAGE[row.source_table];
    if (!cfg || !onNavigate) return;
    onNavigate(cfg.page, row.source_id, { viewOnly: true });
  };

  const openHistory = async (row) => {
    setHistoryModal({ source_table: row.source_table, source_id: row.source_id, loading: true, data: null });
    try {
      const r = await api.get('/approvals/by-source/' + row.source_table + '/' + row.source_id);
      setHistoryModal({ source_table: row.source_table, source_id: row.source_id, loading: false, data: r.data?.success ? r.data.data : null });
    } catch (e) {
      setHistoryModal({ source_table: row.source_table, source_id: row.source_id, loading: false, data: null, error: e.response?.data?.message || e.message });
    }
  };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Onaylanan Belgeler"
        unit="belge"
        count={filtered.length}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Belge no, cari, onaycı ara..."
        onRefresh={load}
        refreshing={loading}
      />

      <FilterBar
        show={showFilterBar}
        onToggle={() => setShowFilterBar(s => !s)}
        values={filters}
        onChange={setFilters}
        fields={[
          { key: 'module', type: 'select', label: 'Modül',
            options: [{ value: '', label: 'Tümü' }, ...Object.entries(MODULE_LABELS).map(([k, v]) => ({ value: k, label: v }))] },
          { key: 'direction', type: 'select', label: 'Yön',
            options: [{ value: '', label: 'Tümü' }, ...Object.entries(DIRECTION_LABELS).map(([k, v]) => ({ value: k, label: v }))] },
          { key: 'date_from', type: 'date', label: 'Onay Başlangıç' },
          { key: 'date_to',   type: 'date', label: 'Onay Bitiş' },
        ]}
      />

      {filtered.length === 0 && !loading ? (
        <EmptyState title="Onaylı belge yok" description="Henüz hiyerarşi sürecinden geçip onaylanmış belge bulunmuyor" compact />
      ) : (
        <div className="stocks-table-wrap">
          <DataTable
            pageKey="approved-documents"
            tableClassName="stocks-table"
            rowClassName="stocks-table-row"
            loading={loading}
            data={filtered}
            rowKey="id"
            onRowClick={openDoc}
            columns={[
              { id: 'module',  header: 'Belge Tipi', accessor: r => MODULE_LABELS[r.module] + ' / ' + DIRECTION_LABELS[r.direction], sortable: true, width: 170 },
              { id: 'docno',   header: 'Belge No', accessor: r => r.document_number || r.internal_number || '—', sortable: true, width: 160,
                cell: r => <span className="stock-code" style={{ fontFamily: 'monospace' }}>{r.document_number || r.internal_number || '—'}</span> },
              { id: 'docdate', header: 'Belge Tarihi', accessor: 'doc_date', sortable: true, width: 110, cell: r => fmtDate(r.doc_date) },
              { id: 'cari',    header: 'Cari', accessor: 'account_title', sortable: true,
                cell: r => <span className="stock-name-cell">{r.account_title || '—'}{r.account_code ? <span style={{ color:'var(--text-tertiary)', fontSize:'0.78rem', marginLeft:6 }}>({r.account_code})</span> : null}</span> },
              { id: 'amount',  header: 'Tutar', accessor: r => parseFloat(r.total_amount) || 0, sortable: true, width: 130,
                headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
                cell: r => <strong style={{ fontFamily: 'monospace' }}>{fmtAmount(r.total_amount || 0)}</strong> },
              { id: 'approver', header: 'Son Onaycı', accessor: 'last_approver_name', sortable: true, width: 180,
                cell: r => r.last_approver_name || <span style={{ color: 'var(--text-tertiary)' }}>—</span> },
              { id: 'approved_at', header: 'Onay Zamanı', accessor: 'last_approved_at', sortable: true, width: 170,
                cell: r => <span style={{ color: 'var(--text-secondary)', fontSize: '0.82rem' }}>{fmtDateTime(r.last_approved_at)}</span> },
              { id: 'hierarchy', header: 'Hiyerarşi', accessor: 'hierarchy_name', sortable: true,
                cell: r => <span style={{ color: 'var(--text-tertiary)', fontSize: '0.82rem' }}>{r.hierarchy_name || '—'}</span> },
              { id: 'actions', header: '', sortable: false, hideable: false, draggable: false, filterable: false, resizable: false, width: 100,
                cellStyle: { textAlign: 'center' },
                cell: r => (
                  <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openHistory(r); }}
                    title="Onay geçmişini görüntüle">
                    📋 Detay
                  </Button>
                ) },
            ]}
          />
        </div>
      )}

      {historyModal && (
        <Modal open onClose={() => setHistoryModal(null)} title="Onay Geçmişi" size="md"
          cancelAction={{ label: 'Kapat', onClick: () => setHistoryModal(null) }}>
          {historyModal.loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}>Yükleniyor...</div>
          ) : historyModal.error ? (
            <div style={{ padding: 12, color: 'var(--danger)' }}>Hata: {historyModal.error}</div>
          ) : !historyModal.data ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-tertiary)' }}>Bu belge için onay kaydı yok.</div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ padding: 10, background: 'var(--bg-secondary)', borderRadius: 8, fontSize: '0.84rem' }}>
                <div><strong>Hiyerarşi:</strong> {historyModal.data.hierarchy_name || '—'}</div>
                <div><strong>Mevcut Durum:</strong> <span style={{ fontWeight: 700, color: historyModal.data.status === 'approved' ? 'var(--success)' : historyModal.data.status === 'rejected' ? 'var(--danger)' : '#b45309' }}>
                  {historyModal.data.status === 'approved' ? '✓ Onaylandı' : historyModal.data.status === 'rejected' ? '✕ Reddedildi' : historyModal.data.status === 'sent_back' ? '↩ Geri Gönderildi' : '⏳ Bekliyor'}
                </span></div>
                {historyModal.data.finalized_at && (
                  <div><strong>Tamamlanma:</strong> {new Date(historyModal.data.finalized_at).toLocaleString('tr-TR')}</div>
                )}
              </div>
              {historyModal.data.actions?.length > 0 ? (
                <div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', marginBottom: 6 }}>İşlem Geçmişi</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                        <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 500 }}>Seviye</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 500 }}>Eylem</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 500 }}>Kullanıcı</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 500 }}>Tarih / Saat</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left', color: 'var(--text-tertiary)', fontWeight: 500 }}>Not</th>
                      </tr>
                    </thead>
                    <tbody>{historyModal.data.actions.map(a => (
                      <tr key={a.id} style={{ borderBottom: '1px solid var(--border-secondary)' }}>
                        <td style={{ padding: '8px' }}>{a.level_no}{a.level_name ? ' — ' + a.level_name : ''}</td>
                        <td style={{ padding: '8px' }}>
                          {(() => {
                            const cfg =
                              a.action === 'approved'  ? { lbl: '✓ Onayladı',     bg: 'rgba(16,185,129,0.12)', clr: 'var(--success)' }
                            : a.action === 'rejected'  ? { lbl: '✕ Reddetti',     bg: 'rgba(239,68,68,0.12)',  clr: 'var(--danger)'  }
                            : a.action === 'revoked'   ? { lbl: '↺ Onayı Geri Aldı', bg: 'rgba(99,102,241,0.12)', clr: '#6366f1' }
                            : a.action === 'sent_back' ? { lbl: '↩ Geri Gönderdi', bg: 'rgba(245,158,11,0.12)', clr: '#b45309' }
                            : { lbl: a.action, bg: 'rgba(100,116,139,0.12)', clr: 'var(--text-secondary)' };
                            return <span style={{ fontSize: '0.74rem', padding: '2px 8px', borderRadius: 'var(--r-full)', fontWeight: 700, background: cfg.bg, color: cfg.clr }}>{cfg.lbl}</span>;
                          })()}
                        </td>
                        <td style={{ padding: '8px' }}>
                          {a.actor_name || a.actor_username || '—'}
                          {a.actor_username && a.actor_name && <span style={{ color: 'var(--text-tertiary)', fontSize: '0.78rem', marginLeft: 4 }}>@{a.actor_username}</span>}
                        </td>
                        <td style={{ padding: '8px', color: 'var(--text-secondary)', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                          {new Date(a.created_at).toLocaleString('tr-TR')}
                        </td>
                        <td style={{ padding: '8px', color: 'var(--text-secondary)', fontStyle: a.note ? 'normal' : 'italic' }}>{a.note || '—'}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)', fontStyle: 'italic' }}>Henüz herhangi bir eylem yok.</div>
              )}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
